// ============================================================
// Router — single source of truth for the Square Code route table.
//
// Hierarchy is the platform spec hierarchy:
//
//   /square                       — MainframeShell
//     /square/dashboard           — DASHBOARD · Home
//     /square/projects            — PROJECTS · index (search, filter, new)
//     /square/projects/:id        — PROJECTS · per-project surface
//     /square/quamspace           — WORKSPACE · QuamSpace
//     /square/comms               — WORKSPACE · Comms (real /api/comms surface)
//     /square/command-center/*    — COMMAND CENTER (Overview/Requests/Files/Status)
//     /square/olivium/*           — OLIVIUM (Signal/Presence/Integrity/Alerts)
//
// /auth/* remain available for the login flow. The shell is
// wrapped in <AccessGate> which enforces both authentication
// (GitHub OAuth) and allowlist approval before rendering.
//
// Backwards-compat redirects keep /domains/* legacy links working
// by routing them into the new /projects surface.
// ============================================================

import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import type { VerticalConfig, FrontendEnv } from '@square/core'
import { peekStashedAuthNext, clearStashedAuthNext, sanitizeAuthNext } from './auth/redirect'

import MainframeShell from './shell/Shell'
import {
  COMMAND_CENTER_ROOT,
  commandCenterPath,
  LEGACY_COMMAND_CENTER_REDIRECTS,
} from './routes/CommandCenter/commandCenterRoutes'

import Login from './routes/Login'
import AuthCallback from './routes/AuthCallback'
import Logout from './routes/Logout'
import AuthGuard from './auth/AuthGuard'
import AccessGate from './auth/AccessGate'
import ClientAccessGate from './auth/ClientAccessGate'

import Dashboard from './routes/Dashboard'

import OliviumTrust from './routes/Olivium/Trust'
import OliviumFeed from './routes/Olivium/Feed'
import OliviumSessions from './routes/Olivium/Sessions'
import OliviumChain from './routes/Olivium/Chain'
import OliviumAlerts from './routes/Olivium/Alerts'

import ProjectsIndex from './routes/Projects/Index'
import ProjectPage from './routes/Projects/ProjectPage'
import ProjectStudio from './routes/Projects/Studio'
import ProjectSite from './routes/Projects/Site'
import ProjectHosting from './routes/Projects/Hosting'
import ProjectComms from './routes/Projects/Comms'
import ProjectSettings from './routes/Projects/Settings'
import ProjectCommand from './routes/Projects/ProjectCommand'
import ModuleShell from './routes/OperatorSurfaces/ModuleShell'
import SecureAccess from './routes/OperatorSurfaces/surfaces/SecureAccess'
import Receipts from './routes/OperatorSurfaces/surfaces/Receipts'
import Invites from './routes/OperatorSurfaces/surfaces/Invites'
import CommandCenterSessions from './routes/OperatorSurfaces/surfaces/Sessions'
import RolesPeople from './routes/OperatorSurfaces/surfaces/RolesPeople'
import TeamWorkspace from './routes/OperatorSurfaces/surfaces/TeamWorkspace'
import WorkCollaborationHub from './routes/OperatorSurfaces/surfaces/WorkCollaborationHub'
import IntakeReceiver from './routes/Intake/IntakeReceiver'
import SquareCodeCommandCenterRoute from './routes/CommandCenter/SquareCodeCommandCenterRoute'
import SquareCodeProjectsRoute from './routes/CommandCenter/SquareCodeProjectsRoute'
import SquareCodeProjectDetailRoute from './routes/CommandCenter/SquareCodeProjectDetailRoute'
import SquareCodeNotificationsRoute from './routes/CommandCenter/SquareCodeNotificationsRoute'
import SquareCodeAgentsOverviewRoute from './routes/CommandCenter/SquareCodeAgentsOverviewRoute'
import SquareCodeAssistantsRoute from './routes/CommandCenter/SquareCodeAssistantsRoute'
import SquareCodeFinanceSpreadsheetRoute from './routes/CommandCenter/SquareCodeFinanceSpreadsheetRoute'
import SquareCodeDiscoveryRoute from './routes/CommandCenter/SquareCodeDiscoveryRoute'
import SquareCodeCalendarRoute from './routes/CommandCenter/SquareCodeCalendarRoute'
import SquareCodeDivisionWorkspaceRoute from './routes/CommandCenter/SquareCodeDivisionWorkspaceRoute'
import SquareCodeOliviumSuiteRoute from './routes/CommandCenter/SquareCodeOliviumSuiteRoute'
import SquareCodeOliviumModuleRoute from './routes/CommandCenter/SquareCodeOliviumModuleRoute'
import SquareCodeOliviumRecordRoute from './routes/CommandCenter/SquareCodeOliviumRecordRoute'
import SquareCodeOperationsRoute from './routes/CommandCenter/SquareCodeOperationsRoute'
import SquareCodeOperationsModuleRoute from './routes/CommandCenter/SquareCodeOperationsModuleRoute'
import SquareCodeDailyMeetingsRoute from './routes/CommandCenter/SquareCodeDailyMeetingsRoute'
import SquareCodeAgentCommandRoute from './routes/CommandCenter/SquareCodeAgentCommandRoute'
import SquareCodeCommunicationsRoute from './routes/CommandCenter/SquareCodeCommunicationsRoute'
import SquareCodeChannelRoute from './routes/CommandCenter/SquareCodeChannelRoute'
import SquareCodeConferenceCallsRoute from './routes/CommandCenter/SquareCodeConferenceCallsRoute'
import SquareCodeConferenceRoomRoute from './routes/CommandCenter/SquareCodeConferenceRoomRoute'
import SquareCodeTeamRoute from './routes/CommandCenter/SquareCodeTeamRoute'
import SquareCodeTeamRoleRoute from './routes/CommandCenter/SquareCodeTeamRoleRoute'
import SquareCodeTeamTaskRoute from './routes/CommandCenter/SquareCodeTeamTaskRoute'
import SquareCodePortalFactoryRoute from './routes/CommandCenter/SquareCodePortalFactoryRoute'
import SquareCodePortalCreateRoute from './routes/CommandCenter/SquareCodePortalCreateRoute'
import SquareCodePortalRegistryRoute from './routes/CommandCenter/SquareCodePortalRegistryRoute'
import SquareCodePortalDetailRoute from './routes/CommandCenter/SquareCodePortalDetailRoute'
import SquareCodePortalModuleRoute from './routes/CommandCenter/SquareCodePortalModuleRoute'
import SquareCodePortalContextRoute from './routes/CommandCenter/SquareCodePortalContextRoute'
import SquareCodePortalRepoRoute from './routes/CommandCenter/SquareCodePortalRepoRoute'
import SquareCodeOwnerPreviewRoute from './routes/CommandCenter/SquareCodeOwnerPreviewRoute'
import SquareCodeRecipientPreviewRoute from './routes/CommandCenter/SquareCodeRecipientPreviewRoute'
import SquareCodeSecureAccessRoute from './routes/CommandCenter/SquareCodeSecureAccessRoute'
import SquareCodeSecureAccessCreateRoute from './routes/CommandCenter/SquareCodeSecureAccessCreateRoute'
import SquareCodeSecureAccessDetailRoute from './routes/CommandCenter/SquareCodeSecureAccessDetailRoute'
import { OWNED_MODULES } from './shell/commandModel'
import ClientAccess from './routes/Projects/ClientAccess'
import WorkspaceShell from './routes/Projects/WorkspaceShells'
import ConnectLayout from './routes/Connect/ConnectLayout'
import ConnectShell from './routes/Connect/ConnectShell'
import SecurePortal from './routes/Commandery/SecurePortal'
import { RecipientPortal } from './routes/SecureAccess/recipient'
import { OliviumIssueRoute } from './routes/CommandCenter/olivium/secure-access'
import QuamspaceIndex from './routes/Quamspace/Index'
import CommsIndex from './routes/Comms/Index'
import TricorderHub from './routes/Tricorder/Hub'
import TricorderClientDetail from './routes/Tricorder/ClientDetail'

import PlatformShell from './routes/PlatformControl/PlatformShell'
import PlatformProjectsView from './routes/PlatformControl/views/Projects'
import GeneralView from './routes/PlatformControl/views/General'
import SecurityView from './routes/PlatformControl/views/Security'
import SSOView from './routes/PlatformControl/views/SSO'
import IntegrationsView from './routes/PlatformControl/views/Integrations'
import OAuthAppsView from './routes/PlatformControl/views/OAuthApps'
import WebhooksView from './routes/PlatformControl/views/Webhooks'
import ApiKeysView from './routes/PlatformControl/views/ApiKeys'
import DatabaseView from './routes/PlatformControl/views/Database'
import StorageView from './routes/PlatformControl/views/Storage'
import RealtimeView from './routes/PlatformControl/views/Realtime'
import AuditLogsView from './routes/PlatformControl/views/AuditLogs'
import PrivacyView from './routes/PlatformControl/views/Privacy'
import LegalView from './routes/PlatformControl/views/Legal'
import InfrastructureView from './routes/PlatformControl/views/Infrastructure'

import Diagnostics from './observability/Diagnostics'

import AccessDenied from './routes/AccessDenied'
import Forbidden from './routes/Forbidden'
import NotFound from './routes/NotFound'

interface AppRoutesProps {
  // VerticalConfig + env are accepted for backwards compatibility
  // with apps/client-starter, even though the new MainframeShell
  // does not currently consume them. Removing them would force a
  // ripple change in the consuming apps.
  vertical?: VerticalConfig
  env?: FrontendEnv
}

// Param-preserving redirect: the retired per-project "commandery" surface
// now lives at :id/command (Project Command).
function ProjectCommanderyRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/square/projects/${id}/command`} replace />
}

// Replace-navigation alias: strips a retired command-center root prefix and
// lands on the canonical root, preserving child path, query, and hash. Aliases
// never render a second command-center tree and never enter browser history.
function CommandCenterAliasRedirect({ root }: { root: string }) {
  const location = useLocation()
  const child = location.pathname.startsWith(root) ? location.pathname.slice(root.length) : ''
  return <Navigate to={`${COMMAND_CENTER_ROOT}${child}${location.search}${location.hash}`} replace />
}

// Retired legacy bookmark → nearest canonical area (query/hash preserved).
// A redirect preserves access to the capability; it does not claim the
// destination has a finished replacement — destinations stay honest.
function LegacyBookmarkRedirect({ to }: { to: string }) {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />
}

// Root landing. Normally sends to the Final Hub Center, but if a pre-login
// destination was stashed and the OAuth provider fell back to the Site URL
// (so the app returned to "/" instead of /auth/callback), honor that
// destination once — this is what lets a gated deep link such as
// /square-code-v2-command-center/projects survive login. peek is
// side-effect-free (safe under strict-mode double render); the stash is
// cleared after commit, so it is honored at most once. Non-authenticated
// visitors have no stash and fall through to the Final Hub, whose AccessGate
// then redirects to login.
function RootLanding() {
  const [target] = useState(() => {
    const stashed = peekStashedAuthNext()
    return stashed ? sanitizeAuthNext(stashed) : COMMAND_CENTER_ROOT
  })
  useEffect(() => {
    clearStashedAuthNext()
  }, [])
  return <Navigate to={target} replace />
}

export default function AppRoutes(_props: AppRoutesProps = {}) {
  // Powered operator surfaces mapped by module key; every other owned module
  // renders an honest ModuleShell placeholder.
  const POWERED_MODULES: Record<string, JSX.Element> = {
    'olv-secure-access': <SecureAccess />,
    'olv-receipts': <Receipts />,
    'sec-people': <RolesPeople />,
    'sec-roles': <RolesPeople />,
    'sec-access-logs': <CommandCenterSessions />,
    'tal-role-assignments': <RolesPeople />,
    'tal-permissions': <RolesPeople />,
    'onb-secure-invites': <Invites />,
    // Survivors of the command-center retirement — these mount at
    // non-command-center /square/* paths and stay in the mainframe shell.
    // (google-workspace was retired 2026-07-19 by owner decision — the
    // owner's own document system replaces the Google vault.)
    'cc-team-workspace': <TeamWorkspace />,
    'cc-work-collaboration': <WorkCollaborationHub />,
  }
  return (
    <Routes>
      {/* Public auth routes — GitHub-only login */}
      <Route path="/auth/login" element={<Login />} />
      <Route path="/auth/signup" element={<Navigate to="/auth/login" replace />} />
      <Route path="/auth/forgot-password" element={<Navigate to="/auth/login" replace />} />
      <Route path="/auth/reset-password" element={<Navigate to="/auth/login" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/logout" element={<Logout />} />
      <Route path="/auth/access-denied" element={<AccessDenied />} />

      {/* Commandery Secure Portal — PUBLIC member access. A member arrives with an
          invite reference + one-time access code (no Square Code login). Redeem +
          verify-session are proxied to the Olivium Secure Share engine and fail
          closed to `not_live` until COMMANDERY_PORTAL_LIVE is enabled server-side.
          Operator invite creation stays in the gated /square operator surface. */}
      <Route path="/commandery/secure-portal/access" element={<SecurePortal mode="access" />} />
      <Route path="/commandery/secure-portal/session" element={<SecurePortal mode="session" />} />
      <Route path="/commandery/access" element={<Navigate to="/square/projects" replace />} />

      {/* Client Intake receiver — PUBLIC. An outside person opens the shareable
          link (/square/intake/:code) with no Square Code login; the invite_code
          is the credential. The receiver fetches the invite's non-secret form
          config and submits into the operator review queue. No operator nav. */}
      <Route path="/square/intake/:code" element={<IntakeReceiver />} />

      {/* New User Portal — PUBLIC, branding-clean recipient onboarding. A recipient
          opens a signed secure link (/portal/:token); the portal auto-resolves the
          invitation (NON-CONSUMING) via the Olivium Secure Share engine and walks an
          honest state machine. It never shows Square Code, never asks for a code on
          the happy path, and fails closed to not_live/provider_required until the
          external engine + COMMANDERY_PORTAL_LIVE are wired server-side. */}
      <Route path="/portal/:token" element={<RecipientPortal />} />

      {/* Root + legacy redirects */}
      <Route path="/" element={<RootLanding />} />
      {/* Legacy /dashboard → the approved Final Hub Center. The frozen
          /square/dashboard surface stays directly reachable (not repointed). */}
      <Route path="/dashboard" element={<Navigate to={COMMAND_CENTER_ROOT} replace />} />
      <Route path="/command-center" element={<CommandCenterAliasRedirect root="/command-center" />} />
      <Route path="/command-center/*" element={<CommandCenterAliasRedirect root="/command-center" />} />
      {/* Retired former-V2 root — compatibility alias, child paths preserved. */}
      <Route path="/square-code-v2-command-center" element={<CommandCenterAliasRedirect root="/square-code-v2-command-center" />} />
      <Route path="/square-code-v2-command-center/*" element={<CommandCenterAliasRedirect root="/square-code-v2-command-center" />} />
      <Route path="/square/command-center-v2" element={<CommandCenterAliasRedirect root="/square/command-center-v2" />} />
      <Route path="/square/command-center-v2/*" element={<CommandCenterAliasRedirect root="/square/command-center-v2" />} />
      <Route path="/olivium" element={<Navigate to="/square/olivium/feed" replace />} />
      <Route path="/olivium/*" element={<Navigate to="/square/olivium/feed" replace />} />

      {/* Legacy /domains/* → new /projects surface */}
      <Route path="/domains" element={<Navigate to="/square/projects" replace />} />
      <Route path="/domains/*" element={<Navigate to="/square/projects" replace />} />
      <Route path="/square/domains" element={<Navigate to="/square/projects" replace />} />
      <Route path="/square/domains/*" element={<Navigate to="/square/projects" replace />} />

      {/* Bare /workspaces/operations → the V2 Operations home (PHASE_2K
          deployment promotion). The in-shell V2 alias
          /square-code-v2-command-center/workspaces/operations already redirects
          to /operations; this top-level shorthand does the same so any legacy
          /workspaces/operations link lands in the V2 Operations surface. */}
      <Route path="/workspaces/operations" element={<Navigate to={commandCenterPath('operations')} replace />} />

      {/* THE Square Code Command Center — the canonical operator surface at
          /square/command-center (owner promotion, 2026-07-19). A full-screen
          "operating room" (own top/bottom bars, fixed overlays) that renders
          OUTSIDE MainframeShell, so its routes are top-level with full paths —
          their static segments outrank the /square shell's catchall. Auth +
          allowlist gated by AccessGate. The legacy MainframeShell command
          center is retired; its bookmarks redirect below. */}
      <Route
        path={COMMAND_CENTER_ROOT}
        element={<AccessGate><SquareCodeCommandCenterRoute /></AccessGate>}
      />
      {/* V2 Projects portfolio (from the command center "Projects" button) and the
          Finance Spreadsheet Workroom (Finance division hot button). Both are
          full-screen standalone surfaces, gated like the command center. */}
      <Route
        path={commandCenterPath('projects')}
        element={<AccessGate><SquareCodeProjectsRoute /></AccessGate>}
      />
      {/* PHASE_2M P1 — URL-routed project summary. Division ids resolve
          statically; anything else loads the live portal record. */}
      <Route
        path={commandCenterPath('projects/:projectId')}
        element={<AccessGate><SquareCodeProjectDetailRoute /></AccessGate>}
      />
      {/* PHASE_2M P4 — "Scheduled" became Notifications: a REAL aggregation
          over the live comms / tasks / Daily-calls / daily-meetings backends
          with real per-channel mark-read. */}
      <Route
        path={commandCenterPath('notifications')}
        element={<AccessGate><SquareCodeNotificationsRoute /></AccessGate>}
      />
      {/* Agent Runtime Reconciliation (2026-07-14) — a single V2-visible
          discoverability entry point to the already-shipped agent system
          (Agent Control Center, Agent Workspace, Activation Doctor, Engine
          Runtime V4, Operations Agent Command). Additive only: forks no
          backend, removes no existing route, reads the same roster API
          Agent Control Center already uses. Every prior route below this
          one is unchanged. */}
      <Route
        path={commandCenterPath('agents')}
        element={<AccessGate><SquareCodeAgentsOverviewRoute /></AccessGate>}
      />
      {/* Functional Agent Runtime — the owner's governed supervision surface:
          pick an agent + project + instruction, drive the durable task, watch
          its live event stream, and approve gated writes. Backed by
          /api/square/agent-runtime/*. */}
      <Route
        path={commandCenterPath('assistants')}
        element={<AccessGate><SquareCodeAssistantsRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('assistants/tasks/:taskId')}
        element={<AccessGate><SquareCodeAssistantsRoute /></AccessGate>}
      />
      {/* Communications + Teams + Tasks (PHASE_2I) — the shared V2 comms/team
          spine on CommandCenterShell. Channels + DMs + membership-scoped messages,
          team roster, and tasks. REALTIME_DEFERRED; nothing fabricated. */}
      <Route
        path={commandCenterPath('communications')}
        element={<AccessGate><SquareCodeCommunicationsRoute mode="channels" /></AccessGate>}
      />
      <Route
        path={commandCenterPath('communications/channels/:id')}
        element={<AccessGate><SquareCodeChannelRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('communications/dms')}
        element={<AccessGate><SquareCodeCommunicationsRoute mode="dms" /></AccessGate>}
      />
      <Route
        path={commandCenterPath('communications/dms/:id')}
        element={<AccessGate><SquareCodeChannelRoute isDm /></AccessGate>}
      />
      {/* Conference Calls (PHASE_2K) — the VIDEO layer (Daily.co), owned by
          Communications. Distinct from the internal Daily Meetings record under
          Operations. Provider status is real (/api/square/daily/status); no
          fabricated active/scheduled calls. */}
      <Route
        path={commandCenterPath('communications/conference-calls')}
        element={<AccessGate><SquareCodeConferenceCallsRoute /></AccessGate>}
      />
      {/* In-app Conference Room — the custom Daily "black studio" call surface.
          Join navigates here; the scoped short-lived token is minted inside the
          room gate and held in memory only (never in this URL). Full-screen,
          AccessGate-protected, no Daily chrome, no new tab. */}
      <Route
        path={commandCenterPath('communications/conference-rooms/:meetingId')}
        element={<AccessGate><SquareCodeConferenceRoomRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('team')}
        element={<AccessGate><SquareCodeTeamRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('team/roles/:roleId')}
        element={<AccessGate><SquareCodeTeamRoleRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('team/tasks/:taskId')}
        element={<AccessGate><SquareCodeTeamTaskRoute /></AccessGate>}
      />
      {/* Olivium Governance Suite home — the Final Hub "Olivium" tile lands
          here (via the workspaces/olivium containment redirect below). Surfaces
          the durable access surfaces (Secure Access / Portal Factory / Project
          Portals) and the seven governance workspaces. Static `olivium` ranks
          above the dynamic olivium/* children, so this exact path matches the
          suite home while olivium/portal-factory, olivium/secure-access, etc.
          keep resolving to their own routes. */}
      <Route
        path={commandCenterPath('olivium')}
        element={<AccessGate><SquareCodeOliviumSuiteRoute /></AccessGate>}
      />
      {/* Olivium Project Portal Factory (PHASE_2J) — reusable powered
          infrastructure. Register a project, apply an Olivium portal shell,
          attach modules, and track readiness / actions / notes / blockers /
          receipts as a REAL saved record (sq_project_portals). Communications /
          Team / Daily Meetings attach from the existing V2 spine (never
          rebuilt). External client access + realtime are DEFERRED. React
          Router v6 ranks static `portal-factory/new` above `portals/:portalId`,
          so the create flow always matches ahead of the dynamic detail route. */}
      <Route
        path={commandCenterPath('olivium/portal-factory')}
        element={<AccessGate><SquareCodePortalFactoryRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('olivium/portal-factory/new')}
        element={<AccessGate><SquareCodePortalCreateRoute /></AccessGate>}
      />
      {/* Owner Preview — owner-only, non-mutating "preview as a persona"
          console. AccessGate alone does not guarantee owner/admin (some
          allowlist configs approve non-admin active members), so the route
          component itself additionally requires isAdmin before rendering
          anything real. Never reachable through ClientAccessGate/Connect;
          renders no external session, mints no token, performs no write. */}
      <Route
        path={commandCenterPath('olivium/portal-factory/preview')}
        element={<AccessGate><SquareCodeOwnerPreviewRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('olivium/portals')}
        element={<AccessGate><SquareCodePortalRegistryRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('olivium/portals/:portalId')}
        element={<AccessGate><SquareCodePortalDetailRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('olivium/portals/:portalId/modules/:moduleId')}
        element={<AccessGate><SquareCodePortalModuleRoute /></AccessGate>}
      />
      {/* End User Portal Activation — "Preview as Recipient": the EXACT
          recipient workspace rendered from the owner-authed preview manifest
          (same builder as the recipient session route, marked preview). No
          invitation, session, or token is created or consumed. */}
      <Route
        path={commandCenterPath('olivium/portals/:portalId/recipient-preview')}
        element={<AccessGate><SquareCodeRecipientPreviewRoute /></AccessGate>}
      />
      {/* PHASE_2L — project context + repo spine (per portal). Static segments
          rank above :portalId/modules/:moduleId, so they always match. Saved
          repo/deploy metadata persists to sq_project_portal_context; live GitHub
          sync + permission automation are honestly deferred. */}
      <Route
        path={commandCenterPath('olivium/portals/:portalId/context')}
        element={<AccessGate><SquareCodePortalContextRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('olivium/portals/:portalId/repo')}
        element={<AccessGate><SquareCodePortalRepoRoute /></AccessGate>}
      />
      {/* PHASE_2L — universal Olivium Secure Access hub (reuses the real,
          admin-gated sq_access_codes spine). Static `new` ranks above
          `:accessId`. */}
      <Route
        path={commandCenterPath('olivium/secure-access')}
        element={<AccessGate><SquareCodeSecureAccessRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('olivium/secure-access/new')}
        element={<AccessGate><SquareCodeSecureAccessCreateRoute /></AccessGate>}
      />
      {/* Phase 1 increment 4 — Olivium-AUTHORITY issuance wizard (control-plane).
          Distinct from /new (native sq_access_codes): this configures + issues an
          invitation through the Olivium Secure Share engine via api.access.create,
          dry-run by default and failing closed to PROVIDER_REQUIRED when unwired. */}
      <Route
        path={commandCenterPath('olivium/secure-access/olivium-issue')}
        element={<AccessGate><OliviumIssueRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('olivium/secure-access/:accessId')}
        element={<AccessGate><SquareCodeSecureAccessDetailRoute /></AccessGate>}
      />
      {/* Olivium governance workspaces (this lane): the seven governance cards.
          Each is a working Universal Record Workspace over the local-session
          governance ledger (durable reads layered in where a backend exists).
          Static per-module list segments rank above the dynamic :module/:recordId
          record route, and the pre-existing static olivium children
          (secure-access, portal-factory, portals) rank above both — so this
          dynamic pair only ever resolves the seven governance modules. */}
      <Route path={commandCenterPath('olivium/audit')} element={<AccessGate><SquareCodeOliviumModuleRoute module="audit" /></AccessGate>} />
      <Route path={commandCenterPath('olivium/integrity')} element={<AccessGate><SquareCodeOliviumModuleRoute module="integrity" /></AccessGate>} />
      <Route path={commandCenterPath('olivium/receipts')} element={<AccessGate><SquareCodeOliviumModuleRoute module="receipts" /></AccessGate>} />
      <Route path={commandCenterPath('olivium/risks')} element={<AccessGate><SquareCodeOliviumModuleRoute module="risks" /></AccessGate>} />
      <Route path={commandCenterPath('olivium/access-logs')} element={<AccessGate><SquareCodeOliviumModuleRoute module="access-logs" /></AccessGate>} />
      <Route path={commandCenterPath('olivium/secure-share')} element={<AccessGate><SquareCodeOliviumModuleRoute module="secure-share" /></AccessGate>} />
      <Route path={commandCenterPath('olivium/permission-review')} element={<AccessGate><SquareCodeOliviumModuleRoute module="permission-review" /></AccessGate>} />
      <Route path={commandCenterPath('olivium/:module/:recordId')} element={<AccessGate><SquareCodeOliviumRecordRoute /></AccessGate>} />
      <Route
        path={commandCenterPath('finance/spreadsheet')}
        element={<AccessGate><SquareCodeFinanceSpreadsheetRoute /></AccessGate>}
      />
      {/* Customer Discovery workroom (from the "Discovery" button) and the Quam
          Calendar workroom (from the "Calendar" button). Full-screen standalone
          surfaces, gated like the command center. Honest local previews. */}
      <Route
        path={commandCenterPath('intake')}
        element={<AccessGate><SquareCodeDiscoveryRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('discovery')}
        element={<Navigate to={commandCenterPath('intake')} replace />}
      />
      <Route
        path={commandCenterPath('calendar')}
        element={<AccessGate><SquareCodeCalendarRoute /></AccessGate>}
      />
      {/* V2 Division Workspaces — the additive "organized doorway" layer. Each
          division links to existing Square Code surfaces (real routes) or shows
          honest PLANNED / SETUP-REQUIRED placeholders. Replaces nothing. */}
      {/* Operations containment (PHASE_2H): the operations division workspace is
          superseded by the dedicated /operations mini-portal flow. Redirect it
          there (static path wins over :divisionId) so normal Operations work
          always stays inside the V2 /operations home — never the legacy shell. */}
      <Route
        path={commandCenterPath('workspaces/operations')}
        element={<Navigate to={commandCenterPath('operations')} replace />}
      />
      {/* Olivium containment (this lane): the Olivium division tile must open the
          real V2 Olivium governance suite, never the generic Command Center
          division view. Static `workspaces/olivium` wins over :divisionId and
          redirects into the suite home. Every other division still uses the
          generic workspace. */}
      <Route
        path={commandCenterPath('workspaces/olivium')}
        element={<Navigate to={commandCenterPath('olivium')} replace />}
      />
      <Route
        path={commandCenterPath('workspaces/:divisionId')}
        element={<AccessGate><SquareCodeDivisionWorkspaceRoute /></AccessGate>}
      />
      {/* V2 Operations — internal mini-portal layer. The Operations home lists
          module cards that open INTERNAL working portals at /operations/:moduleId
          (never another domain). Each portal is a workable area with honest
          PLANNED / SETUP-REQUIRED / BACKEND-DEFERRED states where not yet wired. */}
      <Route
        path={commandCenterPath('operations')}
        element={<AccessGate><SquareCodeOperationsRoute /></AccessGate>}
      />
      {/* Daily Meetings is a REAL persisted workflow (sq_daily_meetings) — its
          own workroom. React Router v6 ranks the static `daily-meetings` segment
          above the dynamic `:moduleId`, so this always matches first regardless
          of order. Every other module still opens the generic mini portal. */}
      <Route
        path={commandCenterPath('operations/daily-meetings')}
        element={<AccessGate><SquareCodeDailyMeetingsRoute /></AccessGate>}
      />
      {/* Agent Command is a real entry point to the canonical command
          dispatcher + a live read of the agent approval queue — its own
          workroom (same static-before-dynamic ranking as daily-meetings). */}
      <Route
        path={commandCenterPath('operations/agent-command')}
        element={<AccessGate><SquareCodeAgentCommandRoute /></AccessGate>}
      />
      <Route
        path={commandCenterPath('operations/:moduleId')}
        element={<AccessGate><SquareCodeOperationsModuleRoute /></AccessGate>}
      />

      {/* Retired legacy command-center bookmarks → nearest canonical area.
          Generated from the route contract; static entries rank above the
          hub's dynamic routes, so e.g. projects/arbuckle resolves here. */}
      {Object.entries(LEGACY_COMMAND_CENTER_REDIRECTS).map(([from, to]) => (
        <Route
          key={from}
          path={commandCenterPath(from)}
          element={<LegacyBookmarkRedirect to={commandCenterPath(to)} />}
        />
      ))}
      <Route
        path={commandCenterPath('projects/arbuckle/*')}
        element={<LegacyBookmarkRedirect to={commandCenterPath('projects')} />}
      />

      {/* Google Workspace Vault — RETIRED (owner decision 2026-07-19; the
          owner's own document system replaces it). Bookmarks land on the
          vault workspace; the Google/LandingAI APIs stay dormant server-side. */}
      <Route
        path="/square/google-workspace"
        element={<LegacyBookmarkRedirect to={commandCenterPath('workspaces/vault')} />}
      />

      {/* Square Code mainframe shell — auth + allowlist gated */}
      <Route path="/square" element={<AccessGate><MainframeShell /></AccessGate>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Projects — real index + per-project journey */}
        <Route path="projects">
          <Route index element={<ProjectsIndex />} />
          {/* /projects/new opens the index with the modal flag set */}
          <Route path="new" element={<ProjectsIndex />} />
          <Route path=":id" element={<ProjectPage />} />
          <Route path=":id/studio" element={<ProjectStudio />} />
          <Route path=":id/site" element={<ProjectSite />} />
          <Route path=":id/hosting" element={<ProjectHosting />} />
          <Route path=":id/comms" element={<ProjectComms />} />
          {/* Operator control plan — only meaningful for the Olivium
              Commandery project; other ids render an honest empty state. */}
          {/* Project Command — the single per-project command surface. */}
          <Route path=":id/command" element={<ProjectCommand />} />
          {/* Retired per-project "commandery" surface → Project Command. */}
          <Route path=":id/commandery" element={<ProjectCommanderyRedirect />} />

          {/* Project Workspace · Phase 1 operator shells (empty states).
              No messages/files/tasks/calendar/deliverables/approvals backend
              is implemented yet — see WorkspaceShells.tsx. */}
          <Route path=":id/workspace"      element={<WorkspaceShell surface="workspace" />} />
          <Route path=":id/messages"       element={<WorkspaceShell surface="messages" />} />
          <Route path=":id/files"          element={<WorkspaceShell surface="files" />} />
          <Route path=":id/deliverables"   element={<WorkspaceShell surface="deliverables" />} />
          <Route path=":id/tasks"          element={<WorkspaceShell surface="tasks" />} />
          <Route path=":id/calendar"       element={<WorkspaceShell surface="calendar" />} />
          <Route path=":id/testing"        element={<WorkspaceShell surface="testing" />} />
          <Route path=":id/approvals"      element={<WorkspaceShell surface="approvals" />} />
          <Route path=":id/activity"       element={<WorkspaceShell surface="activity" />} />
          <Route path=":id/client-access"  element={<ClientAccess />} />

          <Route path=":id/settings" element={<ProjectSettings />} />
        </Route>

        {/* QuamSpace / Workspace — personal synchronized workspace */}
        <Route
          path="workspace"
          element={
            <AuthGuard
              mode="inline"
              title="Sign in to open QuamSpace"
              body="QuamSpace syncs saved work, workspace memory, and upload-backed actions against your authenticated browser session. Sign in to load your workspace instead of falling through to a missing-token API error."
            >
              <QuamspaceIndex />
            </AuthGuard>
          }
        />
        <Route path="quamspace" element={<Navigate to="/square/workspace" replace />} />

        {/* Comms — real /api/comms surface */}
        <Route path="comms" element={<CommsIndex />} />

        {/* Tricorder — operator client management hub */}
        <Route path="tricorder">
          <Route index element={<TricorderHub />} />
          <Route path=":id" element={<TricorderClientDetail />} />
          <Route path=":id/thread/:threadId" element={<TricorderClientDetail />} />
        </Route>

        {/* Platform Control — operator/admin control plane */}
        <Route path="control" element={<PlatformShell />}>
          <Route index element={<Navigate to="general" replace />} />
          <Route path="projects"       element={<PlatformProjectsView />} />
          <Route path="general"        element={<GeneralView />} />
          <Route path="security"       element={<SecurityView />} />
          <Route path="sso"            element={<SSOView />} />
          <Route path="integrations"   element={<IntegrationsView />} />
          <Route path="oauth"          element={<OAuthAppsView />} />
          <Route path="webhooks"       element={<WebhooksView />} />
          <Route path="api-keys"       element={<ApiKeysView />} />
          <Route path="database"       element={<DatabaseView />} />
          <Route path="storage"        element={<StorageView />} />
          <Route path="realtime"       element={<RealtimeView />} />
          <Route path="infrastructure" element={<InfrastructureView />} />
          <Route path="audit"          element={<AuditLogsView />} />
          <Route path="privacy"        element={<PrivacyView />} />
          <Route path="legal"          element={<LegalView />} />
        </Route>
        {/* Legacy /square/admin alias → control */}
        <Route path="admin" element={<Navigate to="/square/control" replace />} />
        <Route path="admin/*" element={<Navigate to="/square/control" replace />} />



        <Route path="olivium">
          <Route index element={<Navigate to="trust" replace />} />
          <Route path="trust"    element={<OliviumTrust />} />
          <Route path="feed"     element={<OliviumFeed />} />
          <Route path="sessions" element={<OliviumSessions />} />
          <Route path="chain"    element={<OliviumChain />} />
          <Route path="alerts"   element={<OliviumAlerts />} />
        </Route>

        {/* Hidden Sentry diagnostics — gated client-side by ?debug=1 */}
        <Route path="__diag" element={<Diagnostics />} />

        {/* Operating-model module routes — generated from COMMAND_MODEL so a
            module is declared once. Owned modules render a powered operator
            surface (POWERED_MODULES) or an honest ModuleShell placeholder. */}
        {/* Command-center module paths are RETIRED (canonical hub owns
            /square/command-center); the filter is a guard so a future
            commandModel entry can never resurrect a legacy mount. */}
        {OWNED_MODULES.filter((m) => !m.path.startsWith('/square/command-center')).map((m) => (
          <Route
            key={m.key}
            path={m.path.replace('/square/', '')}
            element={POWERED_MODULES[m.key] ?? <ModuleShell def={m} />}
          />
        ))}

        <Route path="403" element={<Forbidden />} />
        <Route path="*"   element={<NotFound />} />
      </Route>

      {/* Client Connect — CLIENT-facing project workspace. Separate from the
          operator /square shell and gated by ClientAccessGate (NOT AccessGate).
          Phase 1: ClientAccessGate fails closed for everyone (Client Connect is
          not live), so these shells are unreachable until membership lands. */}
      <Route path="/connect/:projectSlug" element={<ClientAccessGate><ConnectLayout /></ClientAccessGate>}>
        <Route index element={<ConnectShell surface="workspace" />} />
        <Route path="messages"     element={<ConnectShell surface="messages" />} />
        <Route path="files"        element={<ConnectShell surface="files" />} />
        <Route path="deliverables" element={<ConnectShell surface="deliverables" />} />
        <Route path="tasks"        element={<ConnectShell surface="tasks" />} />
        <Route path="calendar"     element={<ConnectShell surface="calendar" />} />
        <Route path="testing"      element={<ConnectShell surface="testing" />} />
        <Route path="approvals"    element={<ConnectShell surface="approvals" />} />
      </Route>

      <Route path="*" element={<Navigate to="/square/dashboard" replace />} />
    </Routes>
  )
}
